import React from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { getToken } from "../api/auth";
import {
  Tool,
  Frequency,
  listTools,
  listToolGroupCounts,
  createTool,
  updateTool,
  deleteTool,
} from "../api/manageToolsApi";
import { useDebouncedUserLookup } from "../hooks/useDebouncedUserLookup";

const FREQUENCIES: Frequency[] = ["Quarterly", "SemiAnnual", "Annual"];

// SharePoint host for the trivago tenant. Matches SP_HOSTNAME in the backend's
// spGraphClient.ts (parseDocLibraryPath / uploadToDocLibrary). docLibraryPath is
// stored as a SERVER-RELATIVE path (e.g. "/sites/Foo/Shared Documents/Access Reviews/Bar"),
// so a bare href would resolve against the SPA origin and hit the in-app router
// (404). Prefix the SP host to get an absolute, browsable URL (F30).
const SP_HOSTNAME = "trvoffice.sharepoint.com";

/**
 * Build an absolute, browsable SharePoint URL from the tool's stored
 * server-relative docLibraryPath. Normalizes leading slash and trailing slash,
 * decodes any pre-existing percent-encoding, then re-encodes each path segment
 * so spaces ("Shared Documents") render correctly. Returns null if the path is
 * empty/blank.
 */
function buildDocLibraryUrl(docLibraryPath: string | null | undefined): string | null {
  let p = (docLibraryPath ?? "").trim();
  if (!p) return null;
  try { p = decodeURIComponent(p); } catch { /* leave as-is if not decodable */ }
  p = p.replace(/\\/g, "/").replace(/\/+$/g, ""); // backslashes -> slashes, strip trailing /
  if (!p.startsWith("/")) p = "/" + p;
  const encoded = p.split("/").map((seg) => (seg ? encodeURIComponent(seg) : seg)).join("/");
  return `https://${SP_HOSTNAME}${encoded}`;
}

interface FormState {
  name: string;
  toolOwnerUpn: string;
  toolOwnerDisplayName: string;
  frequency: Frequency;
  controlId: string;
  stage1Days: string;
  stage2Days: string;
  reminderStartS1: string;
  reminderStartS2: string;
  docLibraryPath: string;
  /** Empty string means "no override" — converted to null on save. */
  overrideS2ReviewerUpn: string;
  /** true = active; false = deactivated (checkbox ticked). */
  isActive: boolean;
}

function defaultForm(): FormState {
  return {
    name: "",
    toolOwnerUpn: "",
    toolOwnerDisplayName: "",
    frequency: "Quarterly",
    controlId: "",
    stage1Days: "20",
    stage2Days: "7",
    reminderStartS1: "7",
    reminderStartS2: "3",
    docLibraryPath: "",
    overrideS2ReviewerUpn: "",
    isActive: true,
  };
}

function formFromTool(tool: Tool): FormState {
  return {
    name: tool.name,
    toolOwnerUpn: tool.toolOwnerUpn,
    toolOwnerDisplayName: tool.toolOwnerDisplayName,
    frequency: tool.frequency,
    controlId: tool.controlId,
    stage1Days: String(tool.stage1Days),
    stage2Days: String(tool.stage2Days),
    reminderStartS1: String(tool.reminderStartS1),
    reminderStartS2: String(tool.reminderStartS2),
    docLibraryPath: tool.docLibraryPath ?? "",
    overrideS2ReviewerUpn: tool.overrideS2ReviewerUpn ?? "",
    isActive: tool.isActive,
  };
}

function errorMessageFromAxios(err: any, fallback: string): string {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const serverMsg = typeof body === "object" && body !== null ? body.error : null;
  if (status === 401 || status === 403) {
    return "You don't have admin access. Members of the AccessReviewAdmins Entra group can manage tools.";
  }
  if (serverMsg) return serverMsg;
  return fallback;
}

type EditMode = null | "create" | { name: string };

const AdminToolsPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [tools,    setTools]    = React.useState<Tool[]>([]);
  const [loading,  setLoading]  = React.useState(true);
  const [error,    setError]    = React.useState<string | null>(null);

  const [editMode,  setEditMode]  = React.useState<EditMode>(null);
  const [form,      setForm]      = React.useState<FormState>(defaultForm());
  const [formError, setFormError] = React.useState<string | null>(null);
  const [saving,    setSaving]    = React.useState(false);
  const [deletingName, setDeletingName] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<Tool | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  // Group-roster counts for the "Groups" column, keyed by tool name. Held
  // separately from `tools` and loaded from its own read-only endpoint: if that
  // call fails, the column renders "—" and the rest of the registry (including
  // create / edit / delete) is entirely unaffected. null = not loaded yet.
  const [groupCounts, setGroupCounts] = React.useState<Map<string, number> | null>(null);

  // ── Override-UPN live preview ────────────────────────────────────────────
  // Hook fires only when editMode is non-null (the form is visible). Calling
  // it unconditionally is fine — when the form is hidden, the value stays
  // whatever it was last set to and nothing is rendered from it. The 300ms
  // debounce matches the spec in Phase 4.7's chunk plan (Chunk F).
  const overrideLookup = useDebouncedUserLookup(form.overrideS2ReviewerUpn, 300);

  // ── Load tools on mount ───────────────────────────────────────────────────
  React.useEffect(() => {
    if (!account) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await getToken(instance, account);
        const fetched = await listTools();
        setTools(fetched.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        setError(errorMessageFromAxios(err, "Failed to load tools. Please refresh the page."));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [account, instance]);

  // Group-roster counts — loaded independently of the tools list, on purpose.
  // A failure here must never set the page-level error or block the table, so
  // it only logs and leaves groupCounts null (column shows "—").
  React.useEffect(() => {
    if (!account) return;
    let cancelled = false;
    async function loadCounts() {
      try {
        await getToken(instance, account);
        const counts = await listToolGroupCounts();
        if (cancelled) return;
        setGroupCounts(new Map(counts.map((c) => [c.toolName, c.groupCount])));
      } catch (err) {
        console.error("Failed to load tool group counts (Groups column will show —):", err);
      }
    }
    loadCounts();
    return () => { cancelled = true; };
  }, [account, instance]);

  // ── Form controls ─────────────────────────────────────────────────────────
  function startCreate() {
    setEditMode("create");
    setForm(defaultForm());
    setFormError(null);
  }

  function startEdit(tool: Tool) {
    setEditMode({ name: tool.name });
    setForm(formFromTool(tool));
    setFormError(null);
  }

  function cancelEdit() {
    setEditMode(null);
    setFormError(null);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const name      = form.name.trim();
    const ownerUpn  = form.toolOwnerUpn.trim();
    const ownerName = form.toolOwnerDisplayName.trim();
    const controlId = form.controlId.trim();
    const docPath   = form.docLibraryPath.trim();
    const overrideUpn = form.overrideS2ReviewerUpn.trim();

    if (!name || !ownerUpn || !ownerName || !controlId) {
      setFormError("Name, owner UPN, owner display name, and control ID are all required.");
      return;
    }

    if (!docPath) {
      setFormError("SharePoint document library path is required.");
      return;
    }

    const s1  = parseInt(form.stage1Days,      10);
    const s2  = parseInt(form.stage2Days,      10);
    const rs1 = parseInt(form.reminderStartS1, 10);
    const rs2 = parseInt(form.reminderStartS2, 10);
    if (Number.isNaN(s1)  || s1  < 1 || s1  > 30) return setFormError("Stage 1 days must be between 1 and 30.");
    if (Number.isNaN(s2)  || s2  < 1 || s2  > 14) return setFormError("Stage 2 days must be between 1 and 14.");
    if (Number.isNaN(rs1) || rs1 < 1 || rs1 > 14) return setFormError("Stage 1 reminder start must be between 1 and 14 days.");
    if (Number.isNaN(rs2) || rs2 < 1 || rs2 > 7)  return setFormError("Stage 2 reminder start must be between 1 and 7 days.");

    // Override UPN — soft validation: if the live preview says malformed or
    // not_found, block the save with a friendly message. The backend would
    // reject it anyway with HTTP 400, but failing early gives clearer feedback.
    if (overrideUpn.length > 0) {
      if (overrideLookup.status === "malformed") {
        return setFormError("Override Stage 2 reviewer UPN is malformed; expected an email-shaped value.");
      }
      if (overrideLookup.status === "not_found") {
        return setFormError("Override Stage 2 reviewer UPN does not exist in Entra.");
      }
      // "loading" is allowed — the backend will revalidate and reject if needed.
      // "error" is allowed — don't block on a transient lookup failure.
    }

    setSaving(true);
    try {
      // Three-state semantic for overrideS2ReviewerUpn:
      //   - empty   → null (clear/no override)
      //   - non-empty → the trimmed UPN string
      const overridePayload: string | null = overrideUpn === "" ? null : overrideUpn;

      if (editMode === "create") {
        const created = await createTool({
          name, toolOwnerUpn: ownerUpn, toolOwnerDisplayName: ownerName,
          frequency: form.frequency, controlId,
          stage1Days: s1, stage2Days: s2,
          reminderStartS1: rs1, reminderStartS2: rs2,
          docLibraryPath: docPath,
          overrideS2ReviewerUpn: overridePayload,
          isActive: form.isActive,
        });
        setTools((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      } else if (editMode && typeof editMode === "object") {
        const updated = await updateTool(editMode.name, {
          toolOwnerUpn: ownerUpn, toolOwnerDisplayName: ownerName,
          frequency: form.frequency, controlId,
          stage1Days: s1, stage2Days: s2,
          reminderStartS1: rs1, reminderStartS2: rs2,
          docLibraryPath: docPath,
          overrideS2ReviewerUpn: overridePayload,
          isActive: form.isActive,
        });
        setTools((prev) => prev.map((t) => t.name === updated.name ? updated : t));
      }
      setEditMode(null);
    } catch (err) {
      setFormError(errorMessageFromAxios(err, "Failed to save. Please try again."));
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  // Two-step, type-to-confirm delete. requestDelete opens the modal; the actual
  // deletion runs only once the admin types the exact tool name. Deleting a tool
  // also removes its group roster and ALL of its DB cycle history (the SharePoint
  // Excel evidence + auditor emails are the retained record and are untouched).
  // Deletion is still blocked server-side while any cycle is active.
  function requestDelete(tool: Tool) {
    setDeleteTarget(tool);
    setDeleteConfirmText("");
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteConfirmText !== deleteTarget.name) return;
    const tool = deleteTarget;
    setDeletingName(tool.name);
    setDeleteError(null);
    try {
      await deleteTool(tool.name);
      setTools((prev) => prev.filter((t) => t.name !== tool.name));
      setDeleteTarget(null);
      setDeleteConfirmText("");
    } catch (err) {
      setDeleteError(errorMessageFromAxios(err, "Failed to delete. Please try again."));
      console.error(err);
    } finally {
      setDeletingName(null);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const editingName = editMode && typeof editMode === "object" ? editMode.name : null;

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Tools Registry</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.92 }}>
            Create, edit, and remove tools. Each tool defines its own review schedule, control ID, and owner.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginLeft: 32 }}>
          <img src="/trivago-logo.png" alt="trivago" style={{ height: 36 }} />
          <span style={{ fontSize: 11, letterSpacing: "0.08em", opacity: 0.85, marginTop: 4 }}>IT Operations</span>
        </div>
      </div>

      <div style={infoBarStyle}>
        <span style={{ fontSize: 16 }}>ℹ️</span>
        <span>
          Tool <strong>name</strong> is immutable — it appears in Slack messages, Excel filenames, and cycle identifiers.
          Choose carefully on creation.
        </span>
      </div>

      {loading ? (
        <p>Loading tools…</p>
      ) : error ? (
        <div style={errorBoxStyle}>{error}</div>
      ) : (
        <>
          {/* Top action bar */}
          {editMode === null && (
            <div style={{ marginBottom: 16 }}>
              <button onClick={startCreate} style={primaryBtnStyle}>+ Add a tool</button>
            </div>
          )}

          {/* Form panel — visible in create or edit mode */}
          {editMode !== null && (
            <form onSubmit={handleSave} style={formPanelStyle}>
              <h3 style={{ marginTop: 0, color: "#0078d4" }}>
                {editMode === "create" ? "Add a new tool" : `Edit ${editingName}`}
              </h3>

              <div style={formGridStyle}>
                <div>
                  <label style={labelStyle}>Name <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    disabled={editMode !== "create" || saving}
                    style={inputStyle}
                  />
                  {editMode !== "create" && <p style={hintStyle}>Name cannot be changed after creation.</p>}
                </div>
                <div>
                  <label style={labelStyle}>Frequency <span style={requiredStyle}>*</span></label>
                  <select
                    value={form.frequency}
                    onChange={(e) => setForm({ ...form, frequency: e.target.value as Frequency })}
                    disabled={saving}
                    style={inputStyle}
                  >
                    {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Owner UPN <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={form.toolOwnerUpn}
                    onChange={(e) => setForm({ ...form, toolOwnerUpn: e.target.value })}
                    disabled={saving}
                    placeholder="owner@trivago.com"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Owner Display Name <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={form.toolOwnerDisplayName}
                    onChange={(e) => setForm({ ...form, toolOwnerDisplayName: e.target.value })}
                    disabled={saving}
                    placeholder="First Last"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Control ID <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={form.controlId}
                    onChange={(e) => setForm({ ...form, controlId: e.target.value })}
                    disabled={saving}
                    placeholder="ITGC-SOX-0001"
                    style={{ ...inputStyle, fontFamily: "monospace" }}
                  />
                </div>
                <div />

                <div>
                  <label style={labelStyle}>Stage 1 days (1–30)</label>
                  <input
                    type="number" min={1} max={30}
                    value={form.stage1Days}
                    onChange={(e) => setForm({ ...form, stage1Days: e.target.value })}
                    disabled={saving}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Stage 2 days (1–14)</label>
                  <input
                    type="number" min={1} max={14}
                    value={form.stage2Days}
                    onChange={(e) => setForm({ ...form, stage2Days: e.target.value })}
                    disabled={saving}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Stage 1 reminders start (days before deadline, 1–14)</label>
                  <input
                    type="number" min={1} max={14}
                    value={form.reminderStartS1}
                    onChange={(e) => setForm({ ...form, reminderStartS1: e.target.value })}
                    disabled={saving}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Stage 2 reminders start (days before deadline, 1–7)</label>
                  <input
                    type="number" min={1} max={7}
                    value={form.reminderStartS2}
                    onChange={(e) => setForm({ ...form, reminderStartS2: e.target.value })}
                    disabled={saving}
                    style={inputStyle}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>SharePoint document library path <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={form.docLibraryPath}
                    onChange={(e) => setForm({ ...form, docLibraryPath: e.target.value })}
                    disabled={saving}
                    placeholder="/sites/Test-Playground-OH-7/Shared Documents/Access Reviews/{toolName}/"
                    style={inputStyle}
                  />
                  <p style={hintStyle}>Server-relative path of the SharePoint document library folder where this tool's Excel reports are uploaded. Format: <code>/sites/&lt;SiteName&gt;/Shared Documents/Access Reviews/&lt;ToolName&gt;</code> — e.g. <code>/sites/Test-Playground-OH-7/Shared Documents/Access Reviews/SmokeTest</code>. A leading slash is optional. Use the real folder names as they appear in SharePoint ("Shared Documents" is the default library).</p>
                </div>

                {/* Phase 4.7 — Stage 2 reviewer override */}
                <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e1dfdd", marginTop: 6, paddingTop: 14 }}>
                  <label style={labelStyle}>
                    Stage 2 reviewer override (UPN) <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={form.overrideS2ReviewerUpn}
                    onChange={(e) => setForm({ ...form, overrideS2ReviewerUpn: e.target.value })}
                    disabled={saving}
                    placeholder="Leave blank to use manager-chain lookup at Stage 2 start"
                    style={inputStyle}
                  />
                  <p style={hintStyle}>
                    If set, this UPN is used as the Stage 2 reviewer instead of the Tool Owner's manager.
                    The override is re-validated when Stage 2 starts; if the UPN no longer exists or has
                    its Entra account disabled at that moment, the system falls back to the manager-chain
                    lookup and posts an admin alert.
                  </p>
                  <OverrideLookupPreview
                    upn={form.overrideS2ReviewerUpn}
                    lookup={overrideLookup}
                  />
                </div>

                {/* Deactivate tool */}
                <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e1dfdd", marginTop: 6, paddingTop: 14 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: saving ? "default" : "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: !e.target.checked })}
                      disabled={saving}
                      style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
                    />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#323130" }}>Deactivate Tool</span>
                      <p style={{ ...hintStyle, marginTop: 3 }}>
                        When ticked, this tool is skipped by the scheduler and <strong>no new review cycles</strong> are
                        created for it — use this for a tool that is no longer in use but that you want to keep in the
                        registry (with its history) rather than delete. It does <strong>not</strong> affect a cycle that
                        is already running: if a review is currently in Stage 1 or Stage 2 it proceeds and completes
                        normally, and deactivation only takes effect for future cycles. Untick to reactivate.
                      </p>
                    </span>
                  </label>
                </div>
              </div>

              {formError && <div style={{ ...errorBoxStyle, marginTop: 16 }}>{formError}</div>}

              <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
                <button type="submit" disabled={saving} style={primaryBtnStyle}>
                  {saving ? "Saving…" : editMode === "create" ? "Create tool" : "Save changes"}
                </button>
                <button type="button" onClick={cancelEdit} disabled={saving} style={secondaryBtnStyle}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <h3 style={sectionHeadingStyle}>
            Tools
            <span style={countBadgeStyle}>{tools.length}</span>
          </h3>
          {tools.length === 0 ? (
            <p style={{ color: "#605e5c", fontStyle: "italic" }}>No tools configured yet. Click "Add a tool" to get started.</p>
          ) : (
            <table style={tableStyle}>
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Owner</th>
                  <th style={thStyle}>Frequency</th>
                  <th style={thStyle} title="Number of Entra groups in this tool's roster. A tool with no groups produces a SkippedNoMembers cycle when it triggers.">Groups</th>
                  <th style={thStyle}>Control ID</th>
                  <th style={thStyle} title="Stage 1 / Stage 2 duration in days">S1 / S2</th>
                  <th style={thStyle} title="Stage 1 / Stage 2 reminders start (days before deadline)">Rem.</th>
                  <th style={thStyle}>Doc lib</th>
                  <th style={thStyle} title="Stage 2 reviewer override UPN — null/empty means use manager-chain lookup">S2 override</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.name} style={{
                    borderBottom: "1px solid #eee",
                    background: editingName === t.name ? "#fff8e1" : "white",
                  }}>
                    <td style={tdStyle}>
                      <div>{t.name}</div>
                      {!t.isActive && (
                        <span style={deactivatedBadgeStyle} title="Deactivated — no new cycles are created for this tool. Any in-flight cycle still completes.">
                          Deactivated
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: 13.5 }}>{t.toolOwnerDisplayName}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{t.toolOwnerUpn}</div>
                    </td>
                    <td style={tdStyle}>{t.frequency}</td>
                    <td style={tdStyle}>
                      <GroupCountCell toolName={t.name} count={groupCounts?.get(t.name)} />
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12.5 }}>{t.controlId}</td>
                    <td style={tdStyle}>{t.stage1Days} / {t.stage2Days}</td>
                    <td style={tdStyle}>{t.reminderStartS1} / {t.reminderStartS2}</td>
                    <td style={tdStyle}>
                      {buildDocLibraryUrl(t.docLibraryPath)
                        ? <a href={buildDocLibraryUrl(t.docLibraryPath)!} target="_blank" rel="noopener noreferrer" style={{ color: "#0078d4" }}>Open</a>
                        : <span style={{ color: "#888" }}>—</span>
                      }
                    </td>
                    <td style={tdStyle}>
                      {t.overrideS2ReviewerUpn
                        ? (
                          <span title={`Stage 2 will be assigned to ${t.overrideS2ReviewerUpn} instead of using manager-chain lookup`}
                                style={overrideBadgeStyle}>
                            {t.overrideS2ReviewerUpn}
                          </span>
                        )
                        : <span style={{ color: "#888" }}>—</span>
                      }
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => startEdit(t)}
                          disabled={editMode !== null || deletingName === t.name}
                          style={{ ...secondaryBtnStyle, opacity: editMode !== null ? 0.5 : 1 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => requestDelete(t)}
                          disabled={editMode !== null || deletingName === t.name}
                          style={{
                            ...primaryBtnStyle,
                            background: "#d13438",
                            opacity: editMode !== null ? 0.5 : (deletingName === t.name ? 0.6 : 1),
                          }}
                        >
                          {deletingName === t.name ? "Removing…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {deleteTarget && (
        <DeleteToolDialog
          tool={deleteTarget}
          confirmText={deleteConfirmText}
          onConfirmTextChange={setDeleteConfirmText}
          onCancel={() => { setDeleteTarget(null); setDeleteConfirmText(""); setDeleteError(null); }}
          onConfirm={confirmDelete}
          deleting={deletingName === deleteTarget.name}
          error={deleteError}
        />
      )}
    </div>
  );
};

// ── Override-UPN live preview subcomponent ──────────────────────────────────
// Renders nothing when the input is empty (idle) so the form panel stays
// quiet until the admin actually types something.
const OverrideLookupPreview: React.FC<{
  upn: string;
  lookup: ReturnType<typeof useDebouncedUserLookup>;
}> = ({ upn, lookup }) => {
  if (upn.trim().length === 0) return null;

  switch (lookup.status) {
    case "idle":
      return null;
    case "malformed":
      return (
        <div style={previewNeutralStyle}>
          <span style={{ color: "#888" }}>Type a complete email-shaped UPN to validate against Entra.</span>
        </div>
      );
    case "loading":
      return (
        <div style={previewNeutralStyle}>
          <span style={{ color: "#888" }}>Looking up in Entra…</span>
        </div>
      );
    case "found": {
      const result = lookup.result!;
      const disabled = result.accountEnabled === false;
      return (
        <div style={disabled ? previewWarnStyle : previewOkStyle}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {disabled ? "⚠ " : "✓ "}
            {result.displayName ?? "(no display name)"}
            {result.jobTitle && (
              <span style={{ fontWeight: 400, color: "#605e5c" }}> — {result.jobTitle}</span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: disabled ? "#5d4a00" : "#107c10" }}>
            {disabled ? (
              <>Account is currently disabled in Entra. If still disabled when Stage 2 starts, the system will fall back to manager-chain lookup and post an admin alert.</>
            ) : (
              <>Found in Entra · Slack ID {result.slackId ? <code style={codeInlineStyle}>{result.slackId}</code> : <span style={{ color: "#a4262c" }}>not resolved (Jira-UserProps / Slack lookup empty)</span>}</>
            )}
          </div>
        </div>
      );
    }
    case "not_found":
      return (
        <div style={previewErrorStyle}>
          ✗ No user found in Entra with this UPN.
        </div>
      );
    case "error":
      return (
        <div style={previewErrorStyle}>
          Lookup failed: {lookup.errorMessage ?? "unknown error"}
        </div>
      );
  }
};

// ── Groups-roster count cell ─────────────────────────────────────
// Shows how many Entra groups the tool owner has added, linking to that tool's
// Groups Roster page. `count === undefined` means the counts request hasn't
// resolved (or failed) — render a neutral "—" rather than implying zero, since
// zero has a specific meaning (owner hasn't populated the roster).
const GroupCountCell: React.FC<{ toolName: string; count: number | undefined }> = ({ toolName, count }) => {
  const rosterUrl = `/tools/${encodeURIComponent(toolName)}/groups`;

  if (count === undefined) {
    return <span style={{ color: "#888" }} title="Group count unavailable">—</span>;
  }

  if (count === 0) {
    return (
      <Link
        to={rosterUrl}
        style={noGroupsBadgeStyle}
        title="No Entra groups added yet. If a cycle triggers now it will complete as SkippedNoMembers. Click to open the Groups Roster."
      >
        No groups
      </Link>
    );
  }

  return (
    <Link
      to={rosterUrl}
      style={{ color: "#0078d4", fontWeight: 600, textDecoration: "none" }}
      title={`${count} group${count === 1 ? "" : "s"} in the roster. Click to open the Groups Roster.`}
    >
      {count} {count === 1 ? "group" : "groups"}
    </Link>
  );
};

// ── Delete confirmation dialog (type-to-confirm) ────────────────────────
const DeleteToolDialog: React.FC<{
  tool: Tool;
  confirmText: string;
  onConfirmTextChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  deleting: boolean;
  error: string | null;
}> = ({ tool, confirmText, onConfirmTextChange, onCancel, onConfirm, deleting, error }) => {
  const matches = confirmText === tool.name;
  return (
    <div style={dialogBackdropStyle} onClick={deleting ? undefined : onCancel}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, color: "#d13438" }}>Delete “{tool.name}”?</h3>
        <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          This permanently removes the tool, its group roster, and all of its cycle history from the
          application database. The archived Excel evidence in SharePoint and the auditor emails are the
          retained record of evidence and are <strong>not</strong> affected.
        </p>
        <div style={deleteWarnStyle}>
          Deletion is blocked while any cycle for this tool is active (Stage 1, Stage 2, or PostProcessing).
          If you only want to stop future cycles while keeping the tool and its history, use
          {" "}<strong>Deactivate Tool</strong> in the edit form instead.
        </div>
        <p style={{ fontSize: 13, marginBottom: 6 }}>
          Type the tool name <code style={codeInlineStyle}>{tool.name}</code> to confirm:
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => onConfirmTextChange(e.target.value)}
          disabled={deleting}
          autoFocus
          placeholder={tool.name}
          style={inputStyle}
        />
        {error && <div style={{ ...errorBoxStyle, marginTop: 12 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onCancel} disabled={deleting} style={secondaryBtnStyle}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!matches || deleting}
            style={{
              ...primaryBtnStyle,
              background: "#d13438",
              opacity: (!matches || deleting) ? 0.5 : 1,
              cursor: (!matches || deleting) ? "default" : "pointer",
            }}
          >
            {deleting ? "Deleting…" : "Delete tool"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 24,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 1300,
  margin: "0 auto",
};

const heroStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  padding: "16px 24px",
  marginBottom: 24,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const infoBarStyle: React.CSSProperties = {
  background: "#eff6fc",
  border: "1px solid #c7e0f4",
  borderRadius: 4,
  padding: "10px 16px",
  marginBottom: 24,
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  color: "#004578",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#a4262c",
  fontSize: 13,
};

const sectionHeadingStyle: React.CSSProperties = {
  borderBottom: "2px solid #0078d4",
  paddingBottom: 8,
  color: "#0078d4",
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 32,
};

const countBadgeStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  borderRadius: 12,
  padding: "1px 10px",
  fontSize: 13,
  fontWeight: 600,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  tableLayout: "fixed",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
  wordBreak: "break-word",
};

const formPanelStyle: React.CSSProperties = {
  background: "#fafbfc",
  border: "1px solid #d1d5da",
  borderRadius: 6,
  padding: 24,
  marginBottom: 24,
};

const formGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "14px 20px",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 600,
  color: "#323130",
  marginBottom: 4,
};

const requiredStyle: React.CSSProperties = {
  color: "#d13438",
  marginLeft: 2,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 4,
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const hintStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: 11.5,
  color: "#888",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  border: "none",
  padding: "8px 18px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtnStyle: React.CSSProperties = {
  background: "white",
  color: "#0078d4",
  border: "1px solid #0078d4",
  padding: "7px 17px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

// Live-preview status boxes for the override-UPN field.
const previewNeutralStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#f3f2f1",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  fontSize: 12.5,
};

const previewOkStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#dff6dd",
  border: "1px solid #92c353",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#107c10",
};

const previewWarnStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#fff4ce",
  border: "1px solid #f9d77e",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#5d4a00",
};

const previewErrorStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#a4262c",
};

const overrideBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  background: "#deecf9",
  color: "#004578",
  borderRadius: 3,
  padding: "1px 6px",
  fontSize: 11.5,
  fontFamily: "monospace",
  wordBreak: "break-all",
};

const codeInlineStyle: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11.5,
  background: "rgba(0,0,0,0.06)",
  padding: "1px 4px",
  borderRadius: 3,
};

const dialogBackdropStyle: React.CSSProperties = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: 80,
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  background: "white",
  borderRadius: 6,
  padding: 24,
  width: "min(520px, 92vw)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  fontFamily: "Segoe UI, sans-serif",
};

const deleteWarnStyle: React.CSSProperties = {
  background: "#fff4ce",
  border: "1px solid #f9d77e",
  borderRadius: 4,
  padding: "10px 14px",
  margin: "0 0 14px",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "#5d4a00",
};

const deactivatedBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: 4,
  background: "#f3f2f1",
  color: "#605e5c",
  border: "1px solid #d2d0ce",
  borderRadius: 8,
  padding: "1px 7px",
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

// Red badge for a tool whose owner has not added any Entra groups yet — the
// state that would produce a SkippedNoMembers cycle if a review triggered now.
const noGroupsBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  background: "#fde7e9",
  color: "#a4262c",
  border: "1px solid #d13438",
  borderRadius: 8,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 700,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

export default AdminToolsPage;
